/**
 * YouTube utility functions
 */

/**
 * Extract YouTube video ID from various YouTube URL formats
 * @param url YouTube URL
 * @returns Video ID or null if invalid
 */
export function extractYouTubeId(url: string): string | null {
	try {
		const normalized = /^(https?:)?\/\//i.test(url) ? url : `https://${url}`;
		const parsed = new URL(normalized);
		const host = parsed.hostname.toLowerCase();
		const pathParts = parsed.pathname.split("/").filter(Boolean);

		// youtube.com/watch?v=VIDEO_ID
		const v = parsed.searchParams.get("v");
		if (v && v.length > 0) return v;

		// youtu.be/VIDEO_ID
		if (host.includes("youtu.be") && pathParts[0]) return pathParts[0];

		// youtube.com/embed/VIDEO_ID | /shorts/VIDEO_ID | /live/VIDEO_ID | /v/VIDEO_ID
		const markerIndex = pathParts.findIndex((part) =>
			["embed", "shorts", "live", "v"].includes(part.toLowerCase()),
		);
		if (markerIndex !== -1 && pathParts[markerIndex + 1]) {
			return pathParts[markerIndex + 1];
		}
	} catch {
		// Fall back to regex parsing below
	}

	const patterns = [
		// Standard watch URL: https://www.youtube.com/watch?v=VIDEO_ID
		/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/,
		// Embed URL: https://www.youtube.com/embed/VIDEO_ID
		/youtube\.com\/embed\/([^&\n?#]+)/,
		// Short URL: https://youtu.be/VIDEO_ID
		/youtu\.be\/([^&\n?#]+)/,
		// Shorts URL: https://www.youtube.com/shorts/VIDEO_ID
		/youtube\.com\/shorts\/([^&\n?#]+)/,
		// Live URL: https://www.youtube.com/live/VIDEO_ID
		/youtube\.com\/live\/([^&\n?#]+)/,
	];

	for (const pattern of patterns) {
		const match = url.match(pattern);
		if (match && match[1]) {
			return match[1];
		}
	}

	return null;
}

/**
 * Get YouTube thumbnail URL from video ID
 * @param videoId YouTube video ID
 * @param quality Thumbnail quality (default, mqdefault, hqdefault, sddefault, maxresdefault)
 * @returns Thumbnail URL
 */
export function getYouTubeThumbnail(
	videoId: string,
	quality: "default" | "mqdefault" | "hqdefault" | "sddefault" | "maxresdefault" = "hqdefault"
): string {
	return `https://img.youtube.com/vi/${videoId}/${quality}.jpg`;
}

/**
 * Get YouTube embed URL from video ID
 * @param videoId YouTube video ID
 * @returns Embed URL
 */
export function getYouTubeEmbedUrl(videoId: string): string {
	return `https://www.youtube.com/embed/${videoId}`;
}

/**
 * Validate if a URL is a YouTube URL
 * @param url URL to validate
 * @returns True if valid YouTube URL
 */
export function isYouTubeUrl(url: string): boolean {
	return extractYouTubeId(url) !== null;
}

